import { useState, useEffect } from 'react';
import { Box, Chip, CircularProgress } from '@mui/material';
import { LibraryBooks } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import { useCollections } from '../hooks/useCollections';
import { ItemCollectionInfo } from '../types/collection';
import { getCatalogDisplayName } from '../utils/stringHelpers';

interface CollectionBadgesProps {
  itemId: string;
  maxVisible?: number;
  compact?: boolean;
  onBadgeClick?: (collectionId: string) => void;
  collections?: ItemCollectionInfo[];
  /**
   * When false, clicking a pill is a no-op (no selectCollection, no navigate).
   * Set to false on non-owner/public views to prevent stale foreign-collection
   * state being written to the viewer's My Objects filter (GH#1160).
   * Defaults to true to preserve backwards-compatible owner behaviour.
   */
  enableNavigation?: boolean;
}

export const CollectionBadges = ({ itemId, maxVisible = 3, compact = false, onBadgeClick, collections: providedCollections, enableNavigation = true }: CollectionBadgesProps) => {
  const { fetchItemCollections, selectCollection } = useCollections();
  const navigate = useNavigate();
  const [fetchedCollections, setFetchedCollections] = useState<ItemCollectionInfo[]>([]);
  const [loading, setLoading] = useState(providedCollections === undefined);

  useEffect(() => {
    if (providedCollections !== undefined) return;
    fetchItemCollections(itemId)
      .then(setFetchedCollections)
      .catch(err => logger.error('Failed to fetch item collections', err))
      .finally(() => setLoading(false));
  }, [itemId, fetchItemCollections, providedCollections]);

  const collections = providedCollections ?? fetchedCollections;

  if (loading) return <CircularProgress size={16} />;
  if (collections.length === 0) return null;

  if (compact) {
    return (
      <Chip
        label={String(collections.length)}
        size="small"
        variant="outlined"
        color="primary"
        icon={<LibraryBooks />}
        onClick={() => {
          if (onBadgeClick && collections[0]) {
            onBadgeClick(collections[0].collectionId);
          } else if (collections[0] && enableNavigation) {
            selectCollection(collections[0].collectionId);
            navigate('/my-objects');
          }
        }}
        sx={{ cursor: 'pointer' }}
      />
    );
  }

  const visible = collections.slice(0, maxVisible);
  const remaining = collections.length - maxVisible;

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {visible.map(collection => (
        <Chip
          key={collection.collectionId}
          label={getCatalogDisplayName(collection.collectionName)}
          size="small"
          variant="outlined"
          color="primary"
          icon={<LibraryBooks />}
          onClick={() => {
            if (onBadgeClick) {
              onBadgeClick(collection.collectionId);
            } else if (enableNavigation) {
              selectCollection(collection.collectionId);
              navigate('/my-objects');
            }
          }}
          sx={{ cursor: 'pointer' }}
        />
      ))}
      {remaining > 0 && (
        <Chip label={`+${remaining} more`} size="small" />
      )}
    </Box>
  );
};
